<template>
  <v-form v-model="valid">
    <v-row>
      <v-col cols="6">
        <v-text-field
          v-model="localGeneralData.name"
          :rules="rules.requiredField"
          :label="`${$t('APPS.ITEM_NAME')} *`"
        ></v-text-field>
        <v-row no-gutters>
          <v-col class="mr-2">
            <v-select
              v-model="localGeneralData.nav_menu"
              multiple
              :items="navItems"
              item-text="name"
              item-value="id"
              :label="`${$t('APPS.NAV_MENU')}`"
              append-outer-icon="mdi-close"
              @click:append-outer="localGeneralData.nav_menu = []"
            >
            </v-select>
          </v-col>
        </v-row>
        <v-select
          v-model="localGeneralData.languages"
          :rules="rules.requiredMultiSelect"
          multiple
          :items="languages"
          item-text="name"
          item-value="id"
          :label="`${$t('APPS.LANGUAGES')} *`"
        ></v-select>
        <v-select
          v-model="localGeneralData.default_language_id"
          :rules="languagesRules"
          :items="languages"
          item-text="name"
          item-value="id"
          :hint="editing ? $t('APPS.NEW_BUILD_NEEDED') : ''"
          persistent-hint
          :label="`${$t('APPS.DEFAULT_LANGUAGE')} *`"
        ></v-select>
        <v-select
          v-model="localGeneralData.additional_options"
          multiple
          :items="['Guest access', 'Registration']"
          :label="`${$t('APPS.ADDITIONAL_OPTIONS')}`"
        ></v-select>
        <v-row no-gutters>
          <v-col class="mr-2">
            <v-text-field
              v-model="localGeneralData.google_app_link"
              :rules="rules.textLine"
              :label="$t('APPS.LINK_GP')"
            />
          </v-col>
          <v-col class="ml-2">
            <v-text-field
              v-model="localGeneralData.apple_app_link"
              :rules="rules.textLine"
              :label="$t('APPS.LINK_AS')"
            />
          </v-col>
          <v-col class="ml-2">
            <v-spacer />
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="6">
        <v-select
          v-model="localGeneralData.disclaimer"
          :rules="rules.requiredSelect"
          :items="disclaimers"
          item-value="id"
          item-text="name"
          :label="`${$t('APPS.DISCLAIMER')} *`"
          append-outer-icon="mdi-close"
          @click:append-outer="localGeneralData.disclaimer = ''"
        ></v-select>
        <v-select
          v-model="localGeneralData.welcome_id"
          :rules="disclaimerRules"
          :items="administrative"
          item-value="id"
          item-text="name"
          :label="`${$t('APPS.WELCOME')}`"
          append-outer-icon="mdi-close"
          @click:append-outer="localGeneralData.welcome_id = ''"
        ></v-select>
        <v-select
          v-model="localGeneralData.impose_content_id"
          :items="impose_content"
          item-value="id"
          :item-text="item => item.name + ' (' + item.url + ')'"
          :label="`${$t('APPS.IMPOSE_CONTENT')}`"
          append-outer-icon="mdi-close"
          @click:append-outer="localGeneralData.impose_content_id = ''"
        >
        </v-select>
        <v-select
          v-model="localGeneralData.notruf_marketing_text_id"
          :rules="notrufRules"
          :items="administrative"
          item-value="id"
          item-text="name"
          :label="`${$t('APPS.NOTRUF')}`"
          append-outer-icon="mdi-close"
          @click:append-outer="localGeneralData.notruf_marketing_text_id = ''"
        ></v-select>
        <v-select
          v-model="localGeneralData.bottom_navigation"
          multiple
          :items="['Notruf', 'About']"
          :label="`${$t('APPS.BOTTOM_NAVIGATION')}`"
        ></v-select>
        <v-select
          v-model="localGeneralData.main_scr_bottom_navigation"
          multiple
          item-value="id"
          item-text="name"
          :items="mainScrBottomNavigationItems"
          :label="`${$t('APPS.MAIN_BOTTOM_NAVIGATION')}`"
        ></v-select>
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="auto">
        <slot name="buttons" :valid="valid" />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { isEmpty, isEqual } from 'lodash';
import {
  requiredTextLine,
  requiredSelectArray,
  requiredSelectNotArray,
  textLine
} from '@/modules/_global/variables/rules.js';
import { mapCacheActions } from 'vuex-cache';
import i18nRefreshData from '@/modules/_global/mixins/i18nRefreshData';
import { mapState } from 'vuex';

export default {
  name: 'AppCreateForm',

  mixins: [i18nRefreshData],

  model: {
    prop: 'generalData',
    event: 'input'
  },

  props: {
    generalData: {
      type: Object,
      required: true
    },

    editing: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    valid: true,

    localGeneralData: { nav_menu: [1, 5, 7, 8] },
    languages: [],
    navMenuItems: [],
    disclaimers: [],
    administrative: [],
    impose_content: [],

    rules: {
      requiredField: requiredTextLine,
      textLine: textLine,
      requiredSelect: requiredSelectNotArray,
      requiredMultiSelect: requiredSelectArray
    }
  }),

  computed: {
    ...mapState('apps', ['navItems']),
    mainScrBottomNavigationItems() {
      return [
        { id: 'News', name: this.$t('APPS.BOTTOM_NAV.NEWS') },
        { id: 'Notruf', name: this.$t('APPS.BOTTOM_NAV.NOTRUF') },
        { id: 'Home', name: this.$t('APPS.BOTTOM_NAV.HOME') },
        { id: 'Whiteboard', name: this.$t('APPS.BOTTOM_NAV.POSTS') }
      ];
    },
    languagesRules() {
      try {
        if (
          !this.localGeneralData.languages.includes(
            this.localGeneralData.default_language_id
          )
        )
          return [this.$t('APPS.INVALID_LANGUAGE')];

        return this.rules.requiredSelect;
      } catch (e) {
        //
      }

      return [true];
    },

    disclaimerRules() {
      try {
        if (this.localGeneralData.additional_options.includes('Registration'))
          return this.rules.requiredSelect;
      } catch (e) {
        //
      }

      return [true];
    },
    notrufRules() {
      try {
        if (this.localGeneralData.bottom_navigation.includes('Notruf'))
          return this.rules.requiredSelect;
      } catch (e) {
        //
      }

      return [true];
    }
  },

  watch: {
    valid: 'generalDataStatus',

    generalData: {
      deep: true,
      handler() {
        if (!isEqual(this.generalData, this.localGeneralData))
          this.setLocalGeneralData();
      }
    },

    localGeneralData: {
      deep: true,
      handler: 'updateGeneralData'
    }
  },

  created() {
    if (!this.navItems.length) {
      this.setNavMenuItems();
    }

    // if we are in 'edit' mode
    this.setLocalGeneralData();

    this.$store
      .dispatch('localization/retrieveLanguages')
      .then(({ data }) => (this.languages = data));

    this.$store.dispatch('appcontent/disclaimers').then(({ data }) => {
      // TODO: handle clearable field to remove disclaimer
      this.disclaimers = data;
    });
    this.$store.dispatch('appcontent/administrative').then(({ data }) => {
      // TODO: handle clearable field to remove disclaimer
      this.administrative = data;
    });
    this.$store.dispatch('appcontent/iframes').then(({ data }) => {
      // TODO: handle clearable field to remove disclaimer
      this.impose_content = data.data;
    });
  },

  methods: {
    ...mapCacheActions('apps', ['getNavMenuItems']),
    refreshData() {
      this.setNavMenuItems();
    },
    setNavMenuItems() {
      this.getNavMenuItems();
    },
    setLocalGeneralData() {
      if (!isEmpty(this.generalData)) {
        try {
          this.localGeneralData = {
            ...this.generalData,
            languages: this.generalData.languages,
            additional_options: this.generalData.additional_options
              ? this.generalData.additional_options
              : null,
            bottom_navigation: this.generalData.bottom_navigation
              ? this.generalData.bottom_navigation
              : null
          };
        } catch (e) {
          //
        }
      }
    },

    generalDataStatus(status) {
      this.$emit('generalDataStatus', status);
    },

    updateGeneralData() {
      this.$emit('input', { ...this.localGeneralData });
    }
  }
};
</script>
